import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X, Rocket, BookOpen, Target, Play, HelpCircle } from "lucide-react";
import type { User } from "@/types";
import { TourGuide } from "@/components/tours/TourGuide";

interface OnboardingGuideProps {
  user: User | null;
  onOnboardingUpdate: (update: any) => void;
}

export default function OnboardingGuide({ user, onOnboardingUpdate }: OnboardingGuideProps) {
  const [showTour, setShowTour] = useState(false);
  
  const handleDismiss = () => {
    onOnboardingUpdate({ dismissed: true });
  };

  const startTour = () => {
    setShowTour(true);
  };

  return (
    <Card className="bg-gradient-primary text-primary-foreground border-0 shadow-glow">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="flex items-center gap-2">
          <Rocket className="h-5 w-5" />
          Welcome to Your Learning Dashboard!
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="text-primary-foreground hover:bg-primary-foreground/20"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm opacity-90">
          Get started with your professional development journey. Here are some quick tips:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <BookOpen className="h-5 w-5 mt-0.5 opacity-80" />
            <div>
              <h4 className="font-medium text-sm">Browse Courses</h4>
              <p className="text-xs opacity-80">Explore our catalog to find relevant training</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Target className="h-5 w-5 mt-0.5 opacity-80" />
            <div>
              <h4 className="font-medium text-sm">Track Progress</h4>
              <p className="text-xs opacity-80">Monitor your PD hours and achievements</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <HelpCircle className="h-5 w-5 mt-0.5 opacity-80" />
            <div>
              <h4 className="font-medium text-sm flex items-center gap-1">
                Video Tutorials
                <Badge variant="secondary" className="text-[10px] px-1 py-0">Soon</Badge>
              </h4>
              <p className="text-xs opacity-80">Step-by-step guides coming soon</p>
            </div>
          </div>
        </div>
        <div className="flex justify-between">
          <Button
            variant="secondary"
            size="sm"
            onClick={startTour}
            className="bg-white/20 text-white border border-white/30 hover:bg-white/30 hover:text-white font-medium backdrop-blur-sm"
          >
            <Play className="h-4 w-4 mr-2" />
            Take Tour
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDismiss}
            className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
          >
            Got it!
          </Button>
        </div>
        
        <TourGuide 
          isOpen={showTour} 
          onClose={() => setShowTour(false)}
          tourType={user?.role === 'admin' ? 'admin' : 'user'}
        />
      </CardContent>
    </Card>
  );
}